import React, { useMemo, useRef, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import Card from "../../../components/ui/Card";
import { Button } from "../../../components/ui";
import { strings } from "../../../strings/strings";

const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["10px", "11px", "12px", "13px", "14px", "16px", "18px", "20px", "24px"];
Quill.register(SizeStyle, true);

const RichTextEditor = ({
  value,
  onChange,
  handleAdd,
  hasContentChanged,
  handleUpdate,
  item,
  onCancel,
  isRtl = false,
  isLoading = false,
}) => {
  const hasContent = item?.id;
  const quillRef = useRef(null);
  const editorValue = value ? String(value) : "";

  useEffect(() => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    if (!quill) return;

    const direction = isRtl ? "rtl" : "ltr";

    if (quill.root) {
      quill.root.setAttribute("dir", direction);
      quill.root.style.direction = direction;
      quill.root.style.textAlign = isRtl ? "right" : "left";
    }

    if (quill.container) {
      quill.container.setAttribute("dir", direction);
    }
    if (!quill.getFormat().size) {
      quill.format("size", "11px");
    }
  }, [isRtl]);

  const modules = useMemo(() => {
    const toolbarConfig = [
      [{ size: ["10px", "11px", "12px", "13px", "14px", "16px", "18px", "20px", "24px"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: ["#000000", "#32517A"] }],
      [{ align: ["", "center", "right", "justify"] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ];

    return {
      toolbar: { container: toolbarConfig },
      history: { delay: 500, maxStack: 100, userOnly: true },
    };
  }, []);

  const formats = useMemo(
    () => ["font", "size", "bold", "italic", "underline", "strike", "color", "align", "list", "indent", "link"],
    []
  );

  return (
    <div className="w-full space-y-3">
      <Card className="overflow-hidden p-0" shadow border>
        <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-3">
          <h3 className="text-sm font-semibold text-secondary">{strings.contentEditor}</h3>
        </div>

        <div className="relative">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editorValue}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={strings.starttowrite}
            className="[&_.ql-container]:min-h-[450px] [&_.ql-container]:max-h-[450px] [&_.ql-container]:overflow-y-auto [&_.ql-editor]:min-h-[450px] [&_.ql-editor]:font-montserrat"
          />
        </div>
      </Card>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
        {onCancel && (
          <Button onClick={onCancel} variant="outline" size="lg" className="order-2 sm:order-1 border-2">
            {strings.cancel}
          </Button>
        )}
        <Button
          onClick={hasContent ? handleUpdate : handleAdd}
          variant="primary"
          size="lg"
          disabled={(hasContent && !hasContentChanged) || isLoading}
          isLoading={isLoading}
          className={`order-1 sm:order-2 px-8 font-semibold ${
            hasContent && !hasContentChanged ? "opacity-50 cursor-not-allowed" : "hover:shadow-md"
          }`}
        >
          {hasContent ? strings.updateContent : strings.saveContent}
        </Button>
      </div>
    </div>
  );
};

export default RichTextEditor;
